import React from 'react';
import { User, Mail, Phone, Globe, Building2, Calendar, Image as ImageIcon, ToggleLeft, ToggleRight } from 'lucide-react';
import Modal from '../ui/Modal';
import Badge from '../ui/Badge';
import { CollapsibleJson } from './CollapsibleJson';

function getFlagEmoji(code) {
    if (!code) return null;
    code = code.toUpperCase();
    return code.replace(/./g, char =>
        String.fromCodePoint(127397 + char.charCodeAt())
    );
}

export const UserDetailModal = ({ user, open, onClose, onStatusToggle }) => {
    if (!user) return null;

    return (
        <Modal open={open} onClose={onClose} className="max-w-2xl w-full">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    {user.photo ? (
                        <img src={user.photo} alt="User" className="h-10 w-10 rounded-full object-cover border dark:border-slate-600"/>
                    ) : (
                        <div className="h-10 w-10 rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center text-gray-400 dark:text-slate-400">
                            <ImageIcon className="h-5 w-5"/>
                        </div>
                    )}
                    <h2 className="text-xl font-semibold text-gray-600 dark:text-slate-200">User Details</h2>
                </div>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 text-xl font-bold">×</button>
            </div>

            <div className="px-6 py-4 space-y-6">
                {/* Basic Information */}
                <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-3">Basic Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center space-x-3">
                            <User className="h-5 w-5 text-gray-400 dark:text-slate-500"/>
                            <div>
                                <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Full Name</p>
                                <p className="text-sm text-gray-900 dark:text-slate-200">{user.firstname} {user.lastname} {getFlagEmoji(user.last_country_code)}</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <Mail className="h-5 w-5 text-gray-400 dark:text-slate-500"/>
                            <div>
                                <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Email</p>
                                <p className="text-sm text-gray-900 dark:text-slate-200">{user.email}</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <Phone className="h-5 w-5 text-gray-400 dark:text-slate-500"/>
                            <div>
                                <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Phone</p>
                                <p className="text-sm text-gray-900 dark:text-slate-200">{user.tel || 'N/A'}</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <Globe className="h-5 w-5 text-gray-400 dark:text-slate-500"/>
                            <div>
                                <p className="text-sm font-medium text-gray-500 dark:text-slate-400">Language</p>
                                <p className="text-sm text-gray-900 dark:text-slate-200">{user.interface_language?.toUpperCase() || 'N/A'}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Additional Fields */}
                <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-3">Additional Details</h3>
                    <div className="flex items-center space-x-3 mb-2">
                        <span className="text-sm font-medium text-gray-500 dark:text-slate-400">Onboarded</span>
                        <span className="text-sm text-gray-900 dark:text-slate-200">{user.onboarded || user.is_onboarded ? 'Yes' : 'No'}</span>
                    </div>
                    <div>
                        <CollapsibleJson data={user.onboarding_data} title="Onboarding Data" />
                    </div>
                </div>

                {/* Status and Actions */}
                <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-3">Status & Actions</h3>
                    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-800 rounded-lg">
                        <div className="flex items-center space-x-3">
                            <span className="text-sm font-medium text-gray-700 dark:text-slate-300">Account Status:</span>
                            <Badge variant={user.status === 1 ? 'success' : 'danger'}>
                                {user.status === 1 ? 'Active' : 'Disabled'}
                            </Badge>
                        </div>
                        {onStatusToggle && (
                        <button
                            onClick={() => onStatusToggle(user.id, user.status === 1 ? 0 : 1)}
                            className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md ${
                                user.status === 1
                                    ? 'text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600'
                                    : 'text-green-700 bg-green-100 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50'
                            } transition-colors duration-200`}
                        >
                            {user.status === 1 ? (
                                <>
                                    <ToggleLeft className="h-4 w-4 mr-1"/>
                                    Disable User
                                </>
                            ) : (
                                <>
                                    <ToggleRight className="h-4 w-4 mr-1"/>
                                    Enable User
                                </>
                            )}
                        </button>
                        )}
                    </div>
                </div>

                {/* Tenant Information */}
                <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-3">Associated Companies</h3>
                    {user.tenants && user.tenants.length > 0 ? (
                        <div className="space-y-3">
                            {user.tenants.map((tenant) => (
                                <div key={tenant.id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-slate-700 rounded-lg">
                                    <div className="flex items-center space-x-3">
                                        <Building2 className="h-5 w-5 text-gray-400 dark:text-slate-500"/>
                                        <div>
                                            <p className="text-sm font-medium text-gray-900 dark:text-slate-200">{tenant.name} </p>
                                            <p className="text-xs text-gray-500 dark:text-slate-400">Joined: {tenant.created_at ? new Date(tenant.created_at).toLocaleDateString() : '-'}</p>
                                        </div>
                                    </div>
                                    {tenant.is_current && (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                                            Current
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500 dark:text-slate-400 italic">No associated companies</p>
                    )}
                </div>

                {/* Timeline Information */}
                <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-3">Timeline</h3>
                    <div className="space-y-2">
                        <div className="flex items-center space-x-3">
                            <Calendar className="h-4 w-4 text-gray-400 dark:text-slate-500"/>
                            <span className="text-sm text-gray-500 dark:text-slate-400">Created:</span>
                            <span className="text-sm text-gray-900 dark:text-slate-200">{user.created_at ? new Date(user.created_at).toLocaleString() : '-'}</span>
                        </div>
                        <div className="flex items-center space-x-3">
                            <Calendar className="h-4 w-4 text-gray-400 dark:text-slate-500"/>
                            <span className="text-sm text-gray-500 dark:text-slate-400">Last Updated:</span>
                            <span className="text-sm text-gray-900 dark:text-slate-200">{user.updated_at ? new Date(user.updated_at).toLocaleString() : '-'}</span>
                        </div>
                        {user.last_login_at && (
                            <div className="flex items-center space-x-3">
                                <Calendar className="h-4 w-4 text-gray-400 dark:text-slate-500"/>
                                <span className="text-sm text-gray-500 dark:text-slate-400">Last Login:</span>
                                <span className="text-sm text-gray-900 dark:text-slate-200">{new Date(user.last_login_at).toLocaleString()}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50">
                <div className="flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:bg-slate-700 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-600"
                    >
                        Close
                    </button>
                </div>
            </div>
        </Modal>
    );
};
